/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ShieldCheck,
  Search,
  Layers,
  MessageSquare,
  Briefcase,
  Zap,
  ArrowRight,
  Star,
  GitCompare,
  Mail,
  BookOpen,
  Users,
  TrendingUp,
  CheckCircle,
  ExternalLink,
} from 'lucide-react';
import { publicApi } from '../utils/api';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const FAQ_ICON_MAP = {
  ShieldCheck,
  Search,
  Briefcase,
  TrendingUp,
};

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const SECTIONS = [
  {
    id: 'trust',
    label: 'Trust & Transparency',
    icon: ShieldCheck,
    accentColor: '#f97316',       // orange  - "Honest"
    bgGlow: 'rgba(249,115,22,0.08)',
    tagColor: 'bg-orange-500/20 text-orange-300',
    faqs: [
      {
        id: 't1',
        tone: 'Honest',
        q: 'Are your recommendations actually unbiased, or are you just promoting tools that pay you the most?',
        a: (
          <>
            <p>Neither fully. We earn affiliate commissions - we disclose every single one on our{' '}
              <Link to="/transparency" className="underline text-orange-300 hover:text-orange-200">/transparency page</Link>.
              Our rankings are determined by fit-for-use-case scoring, not commission rate.</p>
            <p className="mt-3">Concretely: Semrush pays us $200 per sale. Surfer SEO pays us 25%. We still rank Surfer higher
              for SMB content teams because it's the right tool for most of them. The commission we leave on the table by
              being honest is what makes our recommendations credible.</p>
            <p className="mt-3">If we only promoted the highest-paying tools, our recommendations would stop working - and so
              would our business. Wowrolla only survives if you actually get value from what we recommend.</p>
          </>
        ),
      },
      {
        id: 't2',
        tone: 'Confident',
        q: "How do you keep 58 tools updated? Isn't this just a static list?",
        a: (
          <>
            <p>Fair question. We run automated pricing checks weekly on every tool's pricing page. When a price changes,
              our changelog system flags it within 48 hours and updates the tool page.</p>
            <p className="mt-3">Commission rates are verified monthly against affiliate network dashboards. Every tool page
              shows "last verified [date]" - if you see a date older than 60 days, email us and we'll check it same day.</p>
            <p className="mt-3">We've built the infrastructure for this because outdated information is the fastest way to
              lose trust. We found <strong className="text-cyber-yellow">7 pricing changes in the last 30 days alone</strong>.</p>
          </>
        ),
      },
      {
        id: 't3',
        tone: 'Warm',
        q: "I've been burned by 'expert' AI tool recommendations before. Why is this different?",
        a: (
          <>
            <p>Because we've been burned too. Every tool recommendation on Wowrolla goes through the same question we'd
              ask ourselves: <em>"Would I actually pay for this and use it consistently?"</em></p>
            <p className="mt-3">Tools that are impressive in demos but painful in daily use don't make the list - or they
              come with a clear "best for X only" caveat. We also track which tools get removed from stacks by users who
              added them and then deleted them. That data shapes our recommendations more than any sales deck from a vendor.</p>
            <p className="mt-3">You're not the first person to be oversold a demo and underdelivered a product.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'selection',
    label: 'Tool Selection',
    icon: Search,
    accentColor: '#FFE400',       // cyber-yellow - "Direct"
    bgGlow: 'rgba(255,228,0,0.06)',
    tagColor: 'bg-yellow-500/20 text-cyber-yellow',
    faqs: [
      {
        id: 's1',
        tone: 'Direct',
        q: "I'm a team of 3. Are these enterprise tools going to be overkill?",
        a: (
          <>
            <p>Most of them, yes - and we'll tell you exactly which ones. Our fit scoring system rates every tool on a
              solo/SMB/enterprise scale. For a team of 3, we'd typically recommend:</p>
            <ul className="mt-3 space-y-2 list-none">
              {[
                'MailerLite over HubSpot (until you hit $1M ARR)',
                'Copy.ai over Jasper (better per-seat pricing under 5 users)',
                'Systeme.io over ClickFunnels (60% lifetime commission and better value for small funnels)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle size={15} className="text-laser-green mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3">The filter on our{' '}
              <Link to="/find" className="underline text-cyber-yellow hover:text-yellow-200">directory</Link>{' '}
              is designed for this - set your team size and the irrelevant tools dim automatically.
              We don't pretend every tool works for every team.</p>
          </>
        ),
      },
      {
        id: 's2',
        tone: 'Warm',
        q: "What if I start with a tool and it's wrong for us?",
        a: (
          <>
            <p>Then you switch - and we'll help you do it quickly. Most AI tools have 14 - 30 day free trials, and the ones
              worth using have monthly billing.</p>
            <p className="mt-3">The only tools with real switching costs are CRMs (data migration) and email platforms
              (list migration). For those two specifically, our consultants have built migration playbooks.</p>
            <p className="mt-3">Worst case for any tool on Wowrolla: you lose a month's subscription and an afternoon.
              Best case: you find the tool that saves your team 8 hours a week for years. That's a good bet to take.
              Our <Link to="/alternatives/chatgpt" className="underline text-orange-300 hover:text-orange-200">alternatives pages</Link>{' '}
              exist exactly for when your first choice doesn't fit.</p>
          </>
        ),
      },
      {
        id: 's3',
        tone: 'Direct',
        q: 'How do I compare two tools when they seem to do the same thing?',
        a: (
          <>
            <p>Use our{' '}
              <Link to="/compare" className="underline text-cyber-yellow hover:text-yellow-200">compare tool at /compare</Link>{' '}
              - but also use this framework:</p>
            <ol className="mt-3 space-y-2 list-none">
              {[
                'Which one do YOU find easier to demo in 10 minutes?',
                'Which one has better reviews from teams your exact size?',
                'Which one has a longer cookie window as an affiliate - longer windows mean more loyal customers, meaning the tool is better at retaining users.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-cyber-yellow font-bold shrink-0">{i + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>
            <p className="mt-3">If you're still stuck, our comparison pages end with a "Wowrolla verdict for your team size"
              section. And if that's still not enough,{' '}
              <Link to="/consulting" className="underline text-cyber-yellow hover:text-yellow-200">book 15 minutes with us</Link>{' '}
              - we've seen both tools used by 50+ teams and can tell you which one teams like yours actually stay with.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'consulting',
    label: 'Consulting',
    icon: Briefcase,
    accentColor: '#a78bfa',       // purple - "Honest/Direct"
    bgGlow: 'rgba(167,139,250,0.07)',
    tagColor: 'bg-violet-500/20 text-violet-300',
    faqs: [
      {
        id: 'c1',
        tone: 'Honest',
        q: "Can't I just set these tools up myself? Why do I need a consultant?",
        a: (
          <>
            <p>You absolutely can - and for simple tools like MailerLite or Copy.ai, you probably should. They're designed
              to be self-service.</p>
            <p className="mt-3">Where implementation help becomes a genuine investment:</p>
            <ul className="mt-2 space-y-2 list-none">
              {[
                'When you\'re connecting multiple tools (CRM + email + content AI + tracking)',
                'When you need workflow design not just setup',
                'When your team needs to actually change how they work - not just have a new login',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ArrowRight size={14} className="text-violet-400 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3">The difference between "tool installed" and "tool delivering ROI" is almost always
              workflow and team adoption. If you're buying a single tool under $50/month and your team is fewer than 5 people,
              start yourself. If you're building a stack of 3+ tools for a team that has existing habits -{' '}
              <Link to="/consulting" className="underline text-violet-300 hover:text-violet-200">that's where our implementation service pays for itself</Link>,
              usually within 4 weeks.</p>
          </>
        ),
      },
      {
        id: 'c2',
        tone: 'Direct',
        q: "What does your consulting actually include? Is it worth the investment?",
        a: (
          <>
            <p>Here's exactly what you get:</p>
            <ol className="mt-3 space-y-3 list-none">
              {[
                { label: 'Strategy call', detail: '30 minutes to map your current workflows.' },
                { label: 'Tool configuration', detail: 'We set up and connect every tool in your stack, including API integrations.' },
                { label: 'Workflow design', detail: 'Documented processes your team can follow.' },
                { label: 'Team training', detail: 'A 60-minute recorded session.' },
                { label: '30 days of follow-up', detail: 'Any question, same-day answer.' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/30 text-violet-300 text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span><strong className="text-white">{item.label}:</strong> {item.detail}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <p className="text-sm">
                Average time from strategy call to fully operational: <strong className="text-violet-300">12 days</strong>.
                Average team time recovered in month one: <strong className="text-violet-300">30+ hours</strong>.
                At any hourly rate above $50, that's a 4-week payback.
              </p>
            </div>
          </>
        ),
      },
      {
        id: 'c3',
        tone: 'Honest',
        q: 'Do you make more money if I buy the more expensive tools through your consulting?',
        a: (
          <>
            <p>Yes, technically - higher-priced tools generate higher commissions. Which is exactly why we disclose it.</p>
            <p className="mt-3">Our model only works long-term if you get genuine ROI, because our consulting service depends
              on your referral and repeat business. We've had clients where the right answer was MailerLite at $9/month over
              HubSpot at $400/month - we recommended MailerLite.</p>
            <p className="mt-3">If you ever feel our recommendation skews toward a higher-priced option without a clear reason,
              ask us to justify it against a cheaper alternative. We'll either give you a compelling answer or change
              the recommendation. See our full commission rates on the{' '}
              <Link to="/transparency" className="underline text-violet-300 hover:text-violet-200">transparency page</Link>.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'adoption',
    label: 'AI Adoption',
    icon: Zap,
    accentColor: '#00ff87',       // laser-green - "Warm/Direct"
    bgGlow: 'rgba(0,255,135,0.06)',
    tagColor: 'bg-emerald-500/20 text-laser-green',
    faqs: [
      {
        id: 'a1',
        tone: 'Warm',
        q: "Is it too late to start adopting AI tools? Have I missed the boat?",
        a: (
          <>
            <p>You haven't missed the boat - but you have missed first class. The SMBs who moved in 2023 are further along
              the learning curve, but also further along the "expensive mistakes" curve.</p>
            <p className="mt-3">Most of the tools that matter have matured significantly in the last 18 months. Starting now
              means starting with tools that actually work, at prices that have come down, with training data that has improved.</p>
            <p className="mt-3">The actual risk is waiting another 12 months while pretending you'll start "when it's clearer."</p>
            <p className="mt-4">
              <Link to="/my-stack/build" className="underline text-laser-green hover:text-emerald-300">Build your first stack -></Link>
            </p>
          </>
        ),
      },
      {
        id: 'a2',
        tone: 'Direct',
        q: 'Will these tools replace my team members?',
        a: (
          <>
            <p>The honest answer: some roles, yes - eventually. The roles that AI replaces are the ones that are purely
              mechanical: formatting content, basic data entry, standard email sequences, first-draft writing.</p>
            <p className="mt-3">The roles that AI <em>amplifies</em> are the ones that require judgment, client relationships,
              creative direction, and strategy.</p>
            <p className="mt-3">The more useful way to frame it: the tools we recommend don't replace your marketing manager -
              they give your marketing manager <strong className="text-laser-green">10 extra hours a week</strong> to do
              the work that actually grows your business.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'consulting-library',
    label: 'Consulting Library',
    icon: Mail,
    accentColor: '#38bdf8',
    bgGlow: 'rgba(56,189,248,0.08)',
    tagColor: 'bg-sky-500/20 text-sky-300',
    faqs: [
      {
        id: 'l1',
        tone: 'Direct',
        q: 'How do I know if I should explore AI consulting right now?',
        a: (
          <>
            <p>Start the conversation if one workflow is costing your team several hours every week, competitors are moving faster, or you have already tried basic AI tools without turning them into repeatable process improvements.</p>
            <p className="mt-3">You probably do not need consulting yet if the business is still mostly manual, there is no system of record, or there is no clear owner for the workflow you want to change.</p>
          </>
        ),
      },
      {
        id: 'l2',
        tone: 'Honest',
        q: 'What happens after a discovery call?',
        a: (
          <>
            <p>Usually one of three things: we tell you to wait because the business is not ready, we recommend a short audit to map the workflow and ROI, or we scope a contained pilot with clear deliverables.</p>
            <p className="mt-3">The point of the call is diagnosis, not pressure. If there is no strong use case yet, the correct answer is to say that early.</p>
          </>
        ),
      },
      {
        id: 'l3',
        tone: 'Warm',
        q: 'Do you offer small pilot projects before a larger implementation?',
        a: (
          <>
            <p>Yes. Small pilots are usually the right move for SMBs. They reduce risk, make adoption easier, and produce the operational proof needed before a broader rollout.</p>
            <p className="mt-3">The best pilot is one workflow, one owner, and one scorecard. If that works, expansion becomes much easier to justify.</p>
          </>
        ),
      },
      {
        id: 'l4',
        tone: 'Direct',
        q: 'What pricing structure should I expect for a first implementation?',
        a: (
          <>
            <p>Most first projects make sense as a fixed-scope engagement rather than an open-ended retainer. Small workflow automations and AI-assisted operations projects usually need clear boundaries, a timeline, and one measurable target.</p>
            <p className="mt-3">That structure protects both sides: you know what is being delivered, and we know what success is supposed to look like.</p>
          </>
        ),
      },
      {
        id: 'l5',
        tone: 'Honest',
        q: 'Can you work with our existing systems instead of replacing everything?',
        a: (
          <>
            <p>That is usually the preferred approach. Most SMB projects work best when they preserve the current system of record and add a narrow AI or automation layer around the bottleneck.</p>
            <p className="mt-3">Replacing stable core systems is expensive and disruptive. It should only happen when the current stack is the actual constraint.</p>
          </>
        ),
      },
      {
        id: 'l6',
        tone: 'Warm',
        q: 'How long does a typical AI implementation take?',
        a: (
          <>
            <p>Simple workflow improvements can move in a few weeks. More integrated projects take longer because the work is not just tool setup. It includes process design, testing, training, and adoption.</p>
            <p className="mt-3">Speed matters, but the real target is a workflow the team still uses after launch.</p>
          </>
        ),
      },
      {
        id: 'l7',
        tone: 'Direct',
        q: 'What if the AI does not work as expected?',
        a: (
          <>
            <p>That is why contained pilots matter. We define a success condition upfront, test on real work, and keep a fallback path. If the workflow needs adjustment, we adjust it before expanding the scope.</p>
            <p className="mt-3">The goal is not to defend the tool. The goal is to improve the operation.</p>
          </>
        ),
      },
      {
        id: 'l8',
        tone: 'Honest',
        q: 'How do you handle data privacy and security?',
        a: (
          <>
            <p>Data handling should be explicit before implementation starts. That means understanding where data moves, what leaves the current system, who has access, and what the vendor terms actually permit.</p>
            <p className="mt-3">Privacy is not a checkbox added at the end. It is part of the workflow design.</p>
          </>
        ),
      },
      {
        id: 'l9',
        tone: 'Direct',
        q: 'Do you also train our team?',
        a: (
          <>
            <p>Yes, because implementation without training is not implementation. The people operating the workflow need to understand what changed, what to do with exceptions, and how the new process should be measured.</p>
            <p className="mt-3">Good training is short, practical, and tied to live work.</p>
          </>
        ),
      },
      {
        id: 'l10',
        tone: 'Warm',
        q: 'Do you work only with certain industries?',
        a: (
          <>
            <p>The work is often less about industry labels and more about repeatable business problems: intake, triage, document handling, follow-up, reporting, and internal knowledge workflows.</p>
            <p className="mt-3">If the operating problem is clear and the systems are accessible enough to work with, there is usually a path forward.</p>
          </>
        ),
      },
      {
        id: 'l11',
        tone: 'Honest',
        q: 'Why should I trust someone newer to AI consulting?',
        a: (
          <>
            <p>Because good implementation work is not only about model knowledge. It is also about systems thinking, process design, stakeholder management, integration discipline, and honest scoping.</p>
            <p className="mt-3">The real standard is not sounding futuristic. It is delivering a workflow improvement that survives after launch.</p>
          </>
        ),
      },
      {
        id: 'l12',
        tone: 'Direct',
        q: 'Do you guarantee results?',
        a: (
          <>
            <p>No serious consultant can guarantee market outcomes or team behavior. What can be guaranteed is a disciplined process: clear scoping, transparent communication, measurable targets, and implementation decisions tied to evidence instead of hype.</p>
            <p className="mt-3">That is the standard worth paying for.</p>
          </>
        ),
      },
    ],
  },
];

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function ToneBadge({ tone, tagColor }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${tagColor}`}>
      {tone}
    </span>
  );
}

function FAQItem({ faq, accentColor, tagColor, isOpen, onToggle }) {
  return (
    <div
      className="border rounded-xl overflow-hidden transition-colors duration-200"
      style={{
        borderColor: isOpen ? accentColor + '55' : 'var(--border-subtle)',
        backgroundColor: 'var(--bg-elevated)',
      }}
    >
      <button
        className="w-full text-left px-5 py-4 flex items-start gap-3 group"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <ToneBadge tone={faq.tone} tagColor={tagColor} />
        <span
          className="flex-1 font-medium text-sm leading-snug transition-colors"
          style={{ color: isOpen ? 'var(--text-primary)' : 'var(--text-secondary)' }}
        >
          {faq.q}
        </span>
        <ChevronDown
          size={16}
          className="shrink-0 mt-0.5 transition-transform duration-200"
          style={{
            color: accentColor,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: 'hidden' }}
          >
            <div
              className="px-5 pb-5 text-sm leading-relaxed"
              style={{ color: 'var(--text-secondary)', borderTop: `1px solid ${accentColor}22` }}
            >
              <div className="pt-4">{faq.a}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionBlock({ section, openMap, setOpenMap }) {
  const Icon = section.icon;

  const toggle = (id) =>
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      className="mb-10"
    >
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: section.bgGlow, border: `1px solid ${section.accentColor}40` }}
        >
          <Icon size={18} style={{ color: section.accentColor }} />
        </div>
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {section.label}
        </h2>
      </div>

      <div className="space-y-3">
        {section.faqs.map((faq) => (
          <FAQItem
            key={faq.id}
            faq={faq}
            accentColor={section.accentColor}
            tagColor={section.tagColor}
            isOpen={!!openMap[faq.id]}
            onToggle={() => toggle(faq.id)}
          />
        ))}
      </div>
    </motion.div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ inline CTA cards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function CTACard({ icon: Icon, title, body, linkTo, linkLabel, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl p-5 flex flex-col gap-3"
      style={{
        backgroundColor: 'var(--bg-elevated)',
        border: `1px solid ${accent}33`,
        boxShadow: `0 0 24px ${accent}11`,
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: accent + '18', border: `1px solid ${accent}40` }}
      >
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div>
        <p className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>{title}</p>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{body}</p>
      </div>
      <Link
        to={linkTo}
        className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold hover:gap-2.5 transition-all"
        style={{ color: accent }}
      >
        {linkLabel} <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ main page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

export default function FAQPage() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [openMap, setOpenMap] = useState({});
  const [remoteSections, setRemoteSections] = useState([]);
  const [toolCount, setToolCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const loadRemoteFaq = async () => {
      try {
        const response = await publicApi.getFaqContent();
        const sections = response?.data?.sections || [];
        if (!mounted || !sections.length) {
          return;
        }
        const hydratedSections = sections.map((section) => ({
          ...section,
          icon: FAQ_ICON_MAP[section.icon] || MessageSquare,
        }));
        setRemoteSections(hydratedSections);
      } catch {
        console.debug('No dynamic FAQ content available, using static sections.');
      }
    };
    loadRemoteFaq();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadCounts = async () => {
      try {
        const [toolsRes, categoriesRes] = await Promise.all([
          publicApi.getAllTools(false),
          publicApi.getCategories(),
        ]);
        if (!mounted) return;
        setToolCount((toolsRes.data || []).length);
        setCategoryCount((categoriesRes.data || []).length);
      } catch {
        if (!mounted) return;
        setToolCount(0);
        setCategoryCount(0);
      }
    };

    loadCounts();
    return () => {
      mounted = false;
    };
  }, []);

  const sections = useMemo(() => {
    const mergedSections = (() => {
      if (!remoteSections.length) return SECTIONS;

      const staticBySectionId = new Map(SECTIONS.map((section) => [section.id, section]));
      const mergedFromRemote = remoteSections.map((section) => {
        const staticSection = staticBySectionId.get(section.id);
        if (!staticSection) {
          return section;
        }

        const staticFaqs = staticSection.faqs || [];
        const remoteFaqs = section.faqs || [];
        const remoteFaqById = new Map(
          remoteFaqs.map((faq, index) => [faq.id || faq.q || `remote-${section.id}-${index}`, faq])
        );

        const mergedFaqs = staticFaqs.map((faq, index) => {
          const key = faq.id || faq.q || `static-${section.id}-${index}`;
          return remoteFaqById.get(key) || faq;
        });

        const staticKeys = new Set(
          staticFaqs.map((faq, index) => faq.id || faq.q || `static-${section.id}-${index}`)
        );
        const remoteOnlyFaqs = remoteFaqs.filter((faq, index) => {
          const key = faq.id || faq.q || `remote-${section.id}-${index}`;
          return !staticKeys.has(key);
        });

        return {
          ...staticSection,
          ...section,
          faqs: [...mergedFaqs, ...remoteOnlyFaqs],
        };
      });

      const remoteSectionIds = new Set(remoteSections.map((section) => section.id));
      const staticOnlySections = SECTIONS.filter((section) => !remoteSectionIds.has(section.id));
      return [...mergedFromRemote, ...staticOnlySections];
    })();

    return mergedSections.map((section) => ({
      ...section,
      faqs: (section.faqs || []).map((faq) => {
        if (faq.id !== 't2') return faq;
        const dynamicQuestion = toolCount > 0 && categoryCount > 0
          ? `How do you keep ${toolCount} tools across ${categoryCount} categories updated? Isn't this just a static list?`
          : "How do you keep your tools and categories updated? Isn't this just a static list?";

        return {
          ...faq,
          q: dynamicQuestion,
        };
      }),
    }));
  }, [remoteSections, toolCount, categoryCount]);

  const expandAll = () => {
    const next = {};
    sections.forEach((s) => s.faqs.forEach((f) => { next[f.id] = true; }));
    setOpenMap(next);
  };

  const collapseAll = () => setOpenMap({});

  const anyOpen = Object.values(openMap).some(Boolean);

  return (
    <>
      <Helmet>
        <title>FAQ  -  Wowrolla | Honest answers about AI tool recommendations</title>
        <meta
          name="description"
          content="Honest answers to the questions we get asked most: trust, tool selection, consulting, and AI adoption."
        />
      </Helmet>

      {/* â”€â”€ Hero â”€â”€ */}
      <section
        className="relative overflow-hidden pt-24 pb-16 px-4 resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.buyingDecision})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.94)), url(${IMG.hero.buyingDecision})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 40% at 50% 0%, rgba(255,228,0,0.07) 0%, transparent 70%)',
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10 px-4">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-5"
              style={{
                backgroundColor: 'rgba(255,228,0,0.1)',
                border: '1px solid rgba(255,228,0,0.25)',
                color: '#FFE400',
              }}
            >
              <MessageSquare size={12} /> 11 questions we get asked every week
            </span>

            <h1
              className="text-4xl sm:text-5xl font-black mb-4 leading-tight text-left"
              style={{ color: 'var(--text-primary)' }}
            >
              Questions we get asked a lot.{' '}
              <span className="text-cyber-yellow">Answers we're proud to give.</span>
            </h1>
            <p className="text-lg max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              About trust, tool fit, consulting, and whether the AI boat has already sailed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* â”€â”€ Section nav pills â”€â”€ */}
      <div className="sticky top-16 z-20 py-3 px-4"
        style={{ backgroundColor: 'var(--bg-base)', borderBottom: '1px solid var(--border-subtle)' }}
      >
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2">
          <button
            onClick={anyOpen ? collapseAll : expandAll}
            className="px-3 py-1 rounded-full text-xs font-semibold border transition-colors hover:opacity-80"
            style={{
              borderColor: 'var(--border-strong)',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--bg-elevated)',
            }}
          >
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </button>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-colors hover:opacity-80"
              style={{
                backgroundColor: s.bgGlow,
                border: `1px solid ${s.accentColor}40`,
                color: s.accentColor,
              }}
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* â”€â”€ Main content â”€â”€ */}
      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Left: accordions */}
          <div className="lg:col-span-2">
            {sections.map((section) => (
              <div key={section.id} id={section.id}>
                <SectionBlock
                  section={section}
                  openMap={openMap}
                  setOpenMap={setOpenMap}
                />

                {/* Mid-section CTA splitter */}
                {section.id === 'trust' && (
                  <div
                    className="mb-10 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4"
                    style={{
                      border: '1px solid var(--border-subtle)',
                      backgroundColor: 'var(--bg-elevated)',
                    }}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                        Not sure which tool you need first?
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        Our finder asks 4 questions and gives you a personalised shortlist in 60 seconds.
                      </p>
                    </div>
                    <Link
                      to="/find"
                      className="shrink-0 px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 transition-opacity hover:opacity-80"
                      style={{ backgroundColor: '#FFE400', color: '#0a0a0a' }}
                    >
                      Find My Tool <ArrowRight size={14} />
                    </Link>
                  </div>
                )}

                {section.id === 'selection' && (
                  <div
                    className="mb-10 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4"
                    style={{
                      border: '1px solid rgba(0,255,135,0.2)',
                      backgroundColor: 'rgba(0,255,135,0.05)',
                    }}
                  >
                    <GitCompare size={28} className="text-laser-green shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                        Side-by-side comparison
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        Pick up to 3 tools and compare pricing, features, and team fit in one view.
                      </p>
                    </div>
                    <Link
                      to="/compare"
                      className="shrink-0 px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 transition-opacity hover:opacity-80"
                      style={{ backgroundColor: 'rgba(0,255,135,0.15)', color: '#00ff87', border: '1px solid rgba(0,255,135,0.3)' }}
                    >
                      Compare Tools <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right: sticky sidebar CTAs */}
          <div className="hidden lg:block">
            <div className="sticky top-36 space-y-4">
              <CTACard
                icon={Briefcase}
                title="Book a Consulting Call"
                body="30-min strategy call. We map your workflows and recommend the right stack."
                linkTo="/consulting"
                linkLabel="Book now"
                accent="#a78bfa"
              />
              <CTACard
                icon={Layers}
                title="Build Your Stack"
                body="Pick tools for your team size and use case. Takes 3 minutes."
                linkTo="/my-stack/build"
                linkLabel="Start building"
                accent="#FFE400"
              />
              <CTACard
                icon={GitCompare}
                title="Compare Tools"
                body="Still deciding between two tools? See them side by side."
                linkTo="/compare"
                linkLabel="Open compare"
                accent="#00ff87"
              />
              <CTACard
                icon={Mail}
                title="Weekly Newsletter"
                body="AI tool updates, honest reviews, and curated picks every Thursday."
                linkTo="/newsletter"
                linkLabel="Subscribe free"
                accent="#f97316"
              />
              <CTACard
                icon={ExternalLink}
                title="View Transparency Page"
                body="See our disclosure model, commissions, and editorial standards in detail."
                linkTo="/transparency"
                linkLabel="Open transparency"
                accent="#fb923c"
              />
            </div>
          </div>
        </div>

        {/* â”€â”€ Bottom CTA strip â”€â”€ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-4 rounded-3xl p-8 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(167,139,250,0.12) 0%, rgba(255,228,0,0.08) 50%, rgba(0,255,135,0.08) 100%)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Star size={32} className="mx-auto mb-3 text-cyber-yellow" />
          <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
            Still have questions?
          </h2>
          <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We answer every email personally - usually within 2 hours on weekdays. Or book 15 minutes and
            we'll walk through your specific situation live.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/consulting"
              className="px-5 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ backgroundColor: '#a78bfa', color: '#0a0a0a' }}
            >
              <Briefcase size={15} /> Book Consulting
            </Link>
            <Link
              to="/my-stack/build"
              className="px-5 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ backgroundColor: '#FFE400', color: '#0a0a0a' }}
            >
              <Layers size={15} /> Build My Stack
            </Link>
            <Link
              to="/newsletter"
              className="px-5 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)' }}
            >
              <Mail size={15} /> Subscribe to Newsletter
            </Link>
            <Link
              to="/find"
              className="px-5 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
              style={{ backgroundColor: 'rgba(0,255,135,0.12)', color: '#00ff87', border: '1px solid rgba(0,255,135,0.25)' }}
            >
              <Search size={15} /> Find My Tool
            </Link>
          </div>
        </motion.div>

        {/* â”€â”€ Trust footer strip â”€â”€ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8 flex flex-wrap justify-center gap-6 py-6"
          style={{ borderTop: '1px solid var(--border-subtle)' }}
        >
          {[
            { icon: ShieldCheck, label: 'Full commission disclosure' },
            { icon: TrendingUp, label: 'Weekly pricing updates' },
            { icon: Users, label: '2,400+ stacks built' },
            { icon: BookOpen, label: 'Verified tool reviews' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <Icon size={14} className="text-cyber-yellow" />
              {label}
            </div>
          ))}
        </motion.div>
      </main>
    </>
  );
}

